// Utility functions to map API permission responses to UI elements

// Map API tab names to menu item IDs
export const mapTabToMenuId = (tabName) => {
  const tabMapping = {
    overview: 'default',
    publisher_app_report: 'reporting',
    creative_report: 'creative',
    raw_event_report: 'rawevents',
    state_level_report: 'statereport',
    wow_report: 'wow',
    dod_report: 'dod',
    d2c_report: 'd2c'
  };
  return tabMapping[tabName] || null;
};

// Reverse mapping: menu item ID to API tab name
export const mapMenuIdToTab = (menuId) => {
  const reverseMapping = {
    default: 'overview',
    reporting: 'publisher_app_report',
    creative: 'creative_report',
    rawevents: 'raw_event_report',
    statereport: 'state_level_report',
    wow: 'wow_report',
    dod: 'dod_report',
    d2c: 'd2c_report'
  };
  return reverseMapping[menuId] || null;
};

// Map API widget names to widget display names
export const mapWidgetToDisplayName = (widgetName) => {
  const widgetMapping = {
    Impressions: 'Impressions',
    clicks: 'Clicks',
    media_cost: 'Media Cost',
    installs: 'Installs',
    kpi_events: 'KPI Events',
    mmp_installs: 'MMP Installs',
    mmp_events: 'MMP Events',
    skan_installs: 'SKAN Installs'
  };
  return widgetMapping[widgetName] || widgetName;
};

// Check if a tab is allowed for the user with context
export const isTabAllowed = (tabName, userPermissions, currentTimezone = 'UTC', currentDataType = 'nonskan') => {
  // If no permissions object or no tabs array, show all tabs
  if (!userPermissions || !userPermissions.tabs) {
    return true;
  }

  // If tabs array is empty (explicitly set to show all), show all tabs
  if (userPermissions.tabs.length === 0) {
    return true;
  }

  const isAllowed = userPermissions.tabs.includes(tabName);
  return isAllowed;
};

// Check if a widget is allowed for the user
export const isWidgetAllowed = (widgetName, userPermissions) => {
  // If no permissions object or no widgets array, show all widgets
  if (!userPermissions || !userPermissions.widgets) {
    return true;
  }

  // If widgets array is empty (explicitly set to show all), show all widgets
  if (userPermissions.widgets.length === 0) {
    return true;
  }

  const isAllowed = userPermissions.widgets.includes(widgetName);
  return isAllowed;
};

// Filter menu items based on user permissions with context
export const filterMenuItemsByPermissions = (menuItems, userPermissions, currentTimezone = 'UTC', currentDataType = 'nonskan') => {
  console.log('🔍 filterMenuItemsByPermissions - Input:', {
    menuItems: menuItems.map((item) => ({
      id: item.id,
      children: item.children?.map((child) => child.id)
    })),
    userPermissions,
    currentTimezone,
    currentDataType
  });

  // If no permissions object or no tabs array, return all menu items
  if (!userPermissions || !userPermissions.tabs) {
    console.log('🔍 filterMenuItemsByPermissions - No permissions, returning all items');
    return menuItems;
  }

  // If tabs array is empty (explicitly set to show all), return all menu items
  if (userPermissions.tabs.length === 0) {
    console.log('🔍 filterMenuItemsByPermissions - Empty tabs array, returning all items');
    return menuItems;
  }

  const filteredItems = menuItems.map((item) => ({
    ...item,
    children:
      item.children?.filter((child) => {
        // Always show headers and admin items
        if (child.type === 'header' || child.id === 'admin') {
          console.log(`🔍 filterMenuItemsByPermissions - Keeping ${child.id} (header/admin)`);
          return true;
        }

        // Check if the tab is allowed
        const tabName = mapMenuIdToTab(child.id);
        const isAllowed = tabName ? isTabAllowed(tabName, userPermissions, currentTimezone, currentDataType) : true;
        console.log(`🔍 filterMenuItemsByPermissions - ${child.id} -> ${tabName}: ${isAllowed ? 'ALLOWED' : 'BLOCKED'}`);
        return isAllowed;
      }) || []
  }));

  console.log(
    '🔍 filterMenuItemsByPermissions - Output:',
    filteredItems.map((item) => ({
      id: item.id,
      children: item.children?.map((child) => child.id)
    }))
  );
  return filteredItems;
};

// Filter widgets based on user permissions
export const filterWidgetsByPermissions = (widgets, userPermissions) => {
  // If no permissions object or no widgets array, return all widgets
  if (!userPermissions || !userPermissions.widgets) {
    return widgets;
  }

  // If widgets array is empty (explicitly set to show all), return all widgets
  if (userPermissions.widgets.length === 0) {
    return widgets;
  }

  return widgets.filter((widget) => {
    const widgetName = widget.title || widget.name;
    return isWidgetAllowed(widgetName, userPermissions);
  });
};
